/**
 * DE-223 — client-side release-ID rules, mirroring backend/src/releaseId.ts.
 *
 * Deliberately a MIRROR, not an import: the portal and the backend are separate
 * packages with no shared build. The regex source is pinned by a test on both
 * sides (portal: releaseId.test.ts, backend: scripts/check-release-id.ts), so a
 * change to one that isn't mirrored fails a test rather than drifting silently.
 *
 * The point of validating here is a decent message before a round-trip; the
 * backend is still the authority and re-validates every request.
 *
 * Per the Versioning and Release Policy (Confluence space DE): a release ID is
 * NOT a semantic version. `YYYY.MM` names the first published release of a
 * month, `YYYY.MM.N` (N from 1) any later one. Never feed it to a semver parser.
 */

/** Zero-padded month, optional `.N` where N >= 1 (never `.0`). */
export const RELEASE_ID_RE = /^\d{4}\.(0[1-9]|1[0-2])(\.[1-9]\d*)?$/;

/** YYYY.MM.<anything numeric> — the shape of an ID whose `.N` is wrong. */
const RELEASE_ID_SHAPED_RE = /^\d{4}\.(0[1-9]|1[0-2])\.\d+$/;

export function isReleaseId(s: unknown): s is string {
  return typeof s === 'string' && RELEASE_ID_RE.test(s.trim());
}

export interface ParsedReleaseId {
  year: number;
  month: number;
  /** 0 for a bare `YYYY.MM`, else the `.N` suffix. */
  n: number;
}

export function parseReleaseId(s: string): ParsedReleaseId | null {
  const t = s.trim();
  if (!RELEASE_ID_RE.test(t)) return null;
  const [year, month, n] = t.split('.');
  if (!year || !month) return null;
  return { year: Number(year), month: Number(month), n: n ? Number(n) : 0 };
}

/**
 * True for something semver-shaped rather than a release ID — the likeliest
 * operator mistake. A valid release ID is excluded FIRST, because a hotfix ID
 * (`2026.09.1`) also has three numeric parts.
 */
export function looksLikeSemver(s: string): boolean {
  const t = s.trim();
  if (RELEASE_ID_RE.test(t)) return false;
  if (RELEASE_ID_SHAPED_RE.test(t)) return false;
  return /^v?\d+\.\d+\.\d+(?:[-+].*)?$/.test(t);
}

/**
 * Why `s` is not a valid release ID, phrased for the operator who typed it.
 * Returns null when `s` IS valid. Mirrors the backend's explainInvalidReleaseId
 * so the inline hint and the 400 body say the same thing.
 */
export function explainInvalidReleaseId(s: string): string | null {
  const t = (s ?? '').trim();
  if (RELEASE_ID_RE.test(t)) return null;
  if (!t) return 'Enter a release ID, or clear the field to deploy without one.';

  if (RELEASE_ID_SHAPED_RE.test(t)) {
    const stem = t.split('.').slice(0, 2).join('.');
    return `"${t}" has an invalid suffix. The bare "${stem}" IS the first release of `
      + `that month; ".N" counts later releases and starts at 1 (${stem}.1, ${stem}.2, …).`;
  }
  if (looksLikeSemver(t)) {
    return `"${t}" is a software version, not a release ID. A release ID is the calendar `
      + 'identifier YYYY.MM (or YYYY.MM.N for a later release that month) — the part '
      + 'versions themselves stay SemVer.';
  }
  if (/^\d{4}\.\d{1,2}(\.\d+)?$/.test(t)) {
    return `"${t}" is not a valid release ID — the month must be two digits, 01-12 `
      + '(e.g. 2026.09, not 2026.9 and not 2026.13).';
  }
  return `"${t}" is not a valid release ID — expected YYYY.MM or YYYY.MM.N, e.g. 2026.09.`;
}

/** Chronological order. Bare `YYYY.MM` sorts BEFORE `YYYY.MM.1`. */
export function compareReleaseIds(a: string, b: string): number {
  const pa = parseReleaseId(a), pb = parseReleaseId(b);
  if (!pa || !pb) return 0;
  return (pa.year - pb.year) || (pa.month - pb.month) || (pa.n - pb.n);
}

/**
 * What to render where a release ID is expected but absent. A device that never
 * received a published release is the NORMAL case right now, so this must read
 * as a neutral fact, never as an error or a blank cell.
 */
export const NO_RELEASE_LABEL = 'No published release';

/**
 * Human label for the device-reported `release.source` (see telemetry's
 * _resolve_release_id): what answered, so "no release ID" is never ambiguous.
 */
export function describeReleaseSource(source: string | null | undefined): string | null {
  switch (source) {
    case 'managed':
      return 'Assigned by a Portal release and verified applied';
    case 'factory':
      return 'Shipped with the installed image; no Portal release since';
    case 'pending':
      return 'A release is staged but its parts are not applied yet';
    default:
      return null;
  }
}
